/**
 * Utility for managing temporary session identifiers for unauthenticated users.
 * Used to track user behavior in LogRocket before they authenticate,
 * helping debug issues like lockouts that occur during the login flow.
 */

const SESSION_STORAGE_KEY = 'cp_unauthenticated_session_id'
const SESSION_CREATED_KEY = 'cp_unauthenticated_session_created'

/**
 * Generates a unique temporary session ID.
 * Format: unauthenticated_<timestamp>_<random>
 */
function generateSessionId(): string {
  const timestamp = Date.now()
  const random = Math.random().toString(36).substring(2, 10)
  return `unauthenticated_${timestamp}_${random}`
}

/**
 * Gets or creates a temporary session ID for an unauthenticated user.
 * The ID persists in sessionStorage so it survives page refreshes
 * but is cleared when the browser session ends.
 */
export function getOrCreateUnauthenticatedSessionId(): string {
  let sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY)

  if (!sessionId) {
    sessionId = generateSessionId()
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId)
    sessionStorage.setItem(SESSION_CREATED_KEY, new Date().toISOString())
    console.log('Created new unauthenticated session ID:', sessionId)
  }

  return sessionId
}

/**
 * Gets the existing unauthenticated session ID without creating a new one.
 * Returns null if no session exists.
 */
export function getUnauthenticatedSessionId(): string | null {
  return sessionStorage.getItem(SESSION_STORAGE_KEY)
}

/**
 * Gets the timestamp when the unauthenticated session was created.
 */
export function getSessionCreatedTime(): string | null {
  return sessionStorage.getItem(SESSION_CREATED_KEY)
}

/**
 * Clears the unauthenticated session ID.
 * Call this after successful authentication to clean up.
 */
export function clearUnauthenticatedSession(): void {
  const existingId = sessionStorage.getItem(SESSION_STORAGE_KEY)
  if (existingId) {
    console.log('Clearing unauthenticated session ID:', existingId)
  }
  sessionStorage.removeItem(SESSION_STORAGE_KEY)
  sessionStorage.removeItem(SESSION_CREATED_KEY)
}

/**
 * Checks if there's an active unauthenticated session.
 */
export function hasUnauthenticatedSession(): boolean {
  return sessionStorage.getItem(SESSION_STORAGE_KEY) !== null
}
